import { createContext } from 'react';
import { DEFAULT_ZOOM, getMapBoundries } from '../../components/common/Map/utils';
import { MarkerInterface, UrlInterface } from '../../types';
import { fetcher } from '../../api';
import {
  Action,
  BoundaryPosition,
  RootContextInterface,
  State 
} from './types';

export const RootStateContext = createContext<RootContextInterface | undefined>(undefined);

export const initialValues = {
  dataMarkers: [],
  dataMarkersRefetch: true,
  boundariesPositions: {
    northEast: {
      lat: 52.696361078274485,
      lng: -53.349609375
    },
    northWest: {
      lat: 52.696361078274485,
      lng: -137.724609375
    },
    southEast: {
      lat: 23.200960808078566,
      lng: -53.349609375
    },
    southWest: {
      lat: 23.200960808078566,
      lng: -137.724609375
    },
    type: "screen"
  },
  showSearchInThisArea: false,
  isLoading: true,
  mapContext: undefined,
  dataList: [],
  page: 1,
  zoom: DEFAULT_ZOOM,
  totalResults: 0,
  extraMarker: null,
  totalPages: 0,
  currentPage: 1,
  previousApp: null,
  geohash: null,
  isMapMoving: false,
  refetch: {
    shouldRefetch: false,
    geohash: null
  }
} as State;

const getMapPrecision = (zoom: number) => {
  switch (zoom) {
    case 4:
      return '2600km'
    case 5:
      return '2000km'
    case 6:
      return '1900km'
    case 7:
      return '1500km'
    case 8:
      return '1000km'
    case 9:
      return '200km'
    case 10:
      return '100km'
    case 11:
      return '50km'
    case 12:
      return '10km'
    case 13:
      return '5km'
    case 14:
      return '1km'
    case 15:
      return '100m'
    case 16:
      return '50m'
    case 17:
      return '10m'
    case 18:
      return '1m'
    default:
      return '2000km'
  }
}

const getMapPrecisionMobile = (zoom: number, mapContext: any) => {
  const { southEast, southWest } = getMapBoundries(mapContext);
  const horizontalDistance = mapContext.distance(southWest, southEast);
  if (zoom < 3.5) {
    return '2600km'
  }
  if (horizontalDistance * 2 / 1000 < 1) {
    return horizontalDistance + "m"
  }
  return horizontalDistance * 2 / 1000 + "km"
}

export const getGeohashPrecision = (geohash: string) => {
  const precisionLength = geohash.length;

  switch (precisionLength) {
    case 1: return 4;
    case 2: return 7;
    case 3: return 9;
    case 4: return 12;
    case 5: return 14;
    case 6: return 15;
    case 7: return 16;
    case 8: return 17;
    case 9: return 18;
    default: return 4;
  }
}

export const checkBoundaries = ({ lat, lng }: { lat: number, lng: number }) => {
  const validLat = lat > 180 ? 180 : lat < -180 ? -180 : lat
  const validLng = lng > 180 ? 180 : lng < -180 ? -180 : lng
  return {
    lat: validLat,
    lng: validLng
  }
}

export const fetchMapData = ({
  url,
  boundariesPositions,
  zoom,
  filters,
  page = 1,
  geohash,
  myLocation,
  isMobile,
  mapContext,
  precision
}: {
  url: UrlInterface,
  zoom: number,
  boundariesPositions: {
    northEast: BoundaryPosition;
    northWest: BoundaryPosition;
    southEast: BoundaryPosition;
    southWest: BoundaryPosition;
    type: "bucket" | "screen"
  },
  filters: URLSearchParams,
  geohash: string | null,
  mapContext: any;
  myLocation: boolean,
  isMobile?: boolean | undefined
  page?: number,
  precision?: string
}) => {
  const { method, url: actionUrl } = url;
  const {
    northEast,
    northWest,
    southEast,
    southWest
  } = boundariesPositions;

  const objectFilters = Object.fromEntries(filters);

  const getFilters = () => {
    let foo = {};
    for (const [key, value] of Object.entries(objectFilters)) {
      if (key !== "open" && key !== "previous" && key !== "appName" && key !== "claim" && key !== "next" && key !== "claim" && key !== 'rating_from') {
        foo = {
          ...foo,
          [key]: value
        }
      }
      if (key === 'rating_from') {
        foo = {
          ...foo,
          rating_range: {
            rating_to: 5,
            rating_from: value
          }
        }
      }
    }
    return foo
  }
  
  return fetcher({
    method,
    url: actionUrl + `?page=${page}`,
    data: {
      northEast: checkBoundaries(northEast),
      northWest: checkBoundaries(northWest),
      southEast: checkBoundaries(southEast),
      southWest: checkBoundaries(southWest),
      precision: precision ? precision : (isMobile && mapContext) ? getMapPrecisionMobile(zoom, mapContext) : getMapPrecision(zoom),
      numberOfBuckets: 1000,
      filters: getFilters()
    }
  }).then((res) => {
    return {
      ...res,
      searchParams: filters,
      geohash,
      myLocation,
      isMobile,
      page
    }
  }).catch(() => {
    return {
      buckets: [],
      results: [],
      searchParams: filters,
      geohash,
      myLocation,
      isMobile: !!isMobile,
      page: 1,
      totalPages: 0
    }
  })
}

export const clearData = ({
  dispatch
}: {
  dispatch: (action: Action) => void
}) => {
  dispatch({
    type: "setDataList",
    data: []
  });
  dispatch({
    type: "setDataMarkers",
    data: []
  });
  dispatch({
    type: "setExtraMarker",
    data: null,
    mapContext: undefined
  });
}

export const flyOptions = {
  duration: 1,
  // padding: [84, 84]
  // padding: [0, 0]
} as any;

export const flyToMarkers = ({
  buckets,
  mapContext,
  dispatch
}: {
  buckets: MarkerInterface[] | [],
  mapContext: any,
  dispatch: (action: Action) => void,
}) => {
  const locations = buckets.map((each) => {
    return each.coordinates
  })
  let animation;
  if (mapContext) {
    if (locations.length === 1) {
      animation = mapContext.flyTo(locations[0], 12, { duration: 1 });
    }
    else if (locations.length > 1) {
      animation = mapContext.flyToBounds(locations, flyOptions);
    } 
    animation.once("moveend", () => {
      dispatch({
        type: "setShowSearchInThisArea",
        data: false
      })
      dispatch({
        type: "setIsMapMoving",
        data: false
      })
    })
  }
}
